import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, useAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { GroupChats, FileBubble } from "@/pages/GroupChat";
import { toast } from "sonner";
import { MessageSquare, Megaphone, Send, Video, Plus, ArrowLeft, X, Search, Paperclip, CalendarClock, Users } from "lucide-react";

const ROLE_LABEL = { trainee: "Student", assessor: "Instructor", admin: "Program Admin", superadmin: "Super Admin", employer: "Employer" };
const timeStr = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function Avatar({ name, size = "w-9 h-9 text-xs" }) {
  return <span className={`${size} rounded-full bg-teal-800 text-white flex items-center justify-center font-bold shrink-0`}>{name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>;
}

function ContactPicker({ onPick, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  const shown = contacts.filter((c) => (c.name + c.email).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div data-testid="contact-picker" className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 space-y-3 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-extrabold text-slate-900">New message</h2>
          <button data-testid="contact-picker-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="contact-search-input" autoFocus placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full h-10 pl-8 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
        </div>
        <div className="overflow-y-auto divide-y divide-slate-50 -mx-1">
          {shown.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No contacts found</p>}
          {shown.map((c) => (
            <button key={c.id} data-testid={`contact-${c.email}`} onClick={() => onPick(c)}
              className="w-full flex items-center gap-3 px-2 py-2.5 hover:bg-teal-50/50 rounded-xl text-left">
              <Avatar name={c.name} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                <p className="text-[11px] text-slate-400">{ROLE_LABEL[c.role] || c.role} · {c.email}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScheduleDialog({ partnerId, partnerName, onClose, onDone }) {
  const [form, setForm] = useState({ topic: "", date: "", time: "", duration: "30 min" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/messages/with/${partnerId}/schedule`, form);
      toast.success("Session scheduled — added to Live Classes for both of you");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule");
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full h-11 px-3.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div data-testid="schedule-dialog" className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><CalendarClock size={18} className="text-teal-700" /> Schedule video session</h2>
          <button data-testid="schedule-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-500">With <span className="font-semibold text-slate-700">{partnerName}</span> — the session appears in both your Live Classes automatically.</p>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="schedule-topic-input" required placeholder="Topic (e.g., Practical review — Lesson 4)" value={form.topic}
            onChange={(e) => setForm({ ...form, topic: e.target.value })} className={input} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="schedule-date-input" required type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} className={input} />
            <input data-testid="schedule-time-input" required type="time" value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })} className={input} />
          </div>
          <select data-testid="schedule-duration-select" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={`${input} bg-white`}>
            {["15 min", "30 min", "45 min", "60 min"].map((d) => <option key={d}>{d}</option>)}
          </select>
          <button data-testid="schedule-submit-btn" type="submit" disabled={busy}
            className="w-full h-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
            {busy ? "Scheduling…" : "Schedule session"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Thread({ partnerId, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sched, setSched] = useState(false);
  const navigate = useNavigate();
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const isStaff = ["assessor", "admin", "superadmin"].includes(user.role);
  const load = useCallback(() => api.get(`/messages/with/${partnerId}`).then((r) => setData(r.data)).catch(() => {}), [partnerId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/messages/with/${partnerId}`, { text });
      setText("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const startCall = async () => {
    try {
      const { data: msg } = await api.post(`/messages/with/${partnerId}/call`);
      navigate(`/call/${msg.room}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start call");
    }
  };

  const sendFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/messages/with/${partnerId}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="text-slate-500 p-6">Loading…</p>;
  return (
    <div className="flex flex-col h-full" data-testid="message-thread">
      {sched && <ScheduleDialog partnerId={partnerId} partnerName={data.partner.name} onClose={() => setSched(false)} onDone={load} />}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <button data-testid="thread-back-btn" onClick={onBack} className="lg:hidden text-slate-400 hover:text-slate-600"><ArrowLeft size={18} /></button>
        <Avatar name={data.partner.name} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm truncate">{data.partner.name}</p>
          <p className="text-[11px] text-slate-400">{ROLE_LABEL[data.partner.role] || data.partner.role}</p>
        </div>
        {isStaff && (
          <button data-testid="schedule-session-btn" onClick={() => setSched(true)}
            className="h-9 px-3.5 rounded-xl border border-teal-700 text-teal-800 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-teal-50 transition-colors">
            <CalendarClock size={14} /> <span className="hidden sm:inline">Schedule</span>
          </button>
        )}
        <button data-testid="start-video-call-btn" onClick={startCall}
          className="h-9 px-3.5 rounded-xl bg-teal-800 hover:bg-teal-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors">
          <Video size={14} /> <span className="hidden sm:inline">Video call</span>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-slate-50/50">
        {data.messages.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Say hello 👋 — start the conversation</p>}
        {data.messages.map((m) => {
          const mine = m.sender_id === user.id;
          if (m.type === "call" || m.type === "schedule") {
            const isSchedule = m.type === "schedule";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="bg-white border border-teal-200 rounded-2xl p-3.5 max-w-[80%] space-y-2">
                  <p className="text-xs text-slate-600 flex items-center gap-1.5">
                    {isSchedule ? <CalendarClock size={14} className="text-teal-700" /> : <Video size={14} className="text-teal-700" />}
                    {isSchedule ? m.text : (mine ? "You started a video call" : `${m.sender_name} started a video call`)}
                  </p>
                  {isSchedule && <p className="text-[10px] text-emerald-600 font-semibold">Added to Live Classes for both participants</p>}
                  <button data-testid={`join-call-${m.id}`} onClick={() => navigate(`/call/${m.room}`)}
                    className="h-8 px-3 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-xs font-semibold">{isSchedule ? "Open session room" : "Join video call"}</button>
                  <p className="text-[10px] text-slate-400">{timeStr(m.created_at)}</p>
                </div>
              </div>
            );
          }
          if (m.type === "file") {
            return (
              <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <FileBubble m={m} mine={mine} />
                <p className="text-[10px] text-slate-400 mt-0.5 mx-1">{timeStr(m.created_at)}</p>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`rounded-2xl px-3.5 py-2 max-w-[80%] ${mine ? "bg-teal-800 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-0.5 ${mine ? "text-teal-200" : "text-slate-400"}`}>{timeStr(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-slate-100">
        <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={sendFile} data-testid="chat-file-input" />
        <button type="button" data-testid="chat-attach-btn" onClick={() => fileRef.current?.click()} disabled={busy}
          className="h-11 w-11 rounded-xl border border-slate-200 text-slate-500 hover:text-teal-700 hover:border-teal-300 flex items-center justify-center transition-colors disabled:opacity-40">
          <Paperclip size={17} />
        </button>
        <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          className="flex-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
        <button data-testid="send-message-btn" type="submit" disabled={busy || !text.trim()}
          className="h-11 w-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors">
          <Send size={17} />
        </button>
      </form>
    </div>
  );
}

function Announcements() {
  const { user } = useAuth();
  const { pick } = useLang();
  const [items, setItems] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [form, setForm] = useState({ track_id: "", text: "" });
  const [busy, setBusy] = useState(false);
  const isStaff = ["assessor", "admin", "superadmin"].includes(user.role);
  const load = () => api.get("/announcements").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); if (isStaff) api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {}); }, []); // eslint-disable-line

  const post = async (e) => {
    e.preventDefault();
    if (!form.track_id || !form.text.trim()) return;
    setBusy(true);
    try {
      await api.post("/announcements", form);
      setForm({ track_id: "", text: "" });
      toast.success("Announcement posted to all enrolled students");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="announcements-panel">
      {isStaff && (
        <form onSubmit={post} className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2"><Megaphone size={15} className="text-amber-600" /> Post a course announcement</h2>
          <select data-testid="announcement-track-select" value={form.track_id} onChange={(e) => setForm({ ...form, track_id: e.target.value })} required
            className="w-full h-11 px-3 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-600">
            <option value="">Select course…</option>
            {tracks.map((t) => <option key={t.id} value={t.id}>{pick(t.title)}</option>)}
          </select>
          <textarea data-testid="announcement-text-input" rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required
            placeholder="Write your announcement — every student enrolled in this course will see it…"
            className="w-full rounded-xl border border-slate-200 p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
          <button data-testid="announcement-post-btn" type="submit" disabled={busy}
            className="h-10 px-5 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors">{busy ? "Posting…" : "Post announcement"}</button>
        </form>
      )}
      {!items ? <p className="text-slate-500">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-slate-400 bg-white rounded-2xl border border-slate-200/80 p-6 text-center">No announcements yet{user.role === "trainee" ? " for your enrolled courses" : ""}.</p>
      ) : items.map((a) => (
        <div key={a.id} data-testid={`announcement-${a.id}`} className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-1.5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">{pick(a.track_title)}</span>
            <span className="text-[11px] text-slate-400">{timeStr(a.created_at)}</span>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{a.text}</p>
          <p className="text-xs text-slate-400">— {a.author_name} ({ROLE_LABEL[a.author_role] || a.author_role})</p>
        </div>
      ))}
    </div>
  );
}

export default function Messages() {
  const [tab, setTab] = useState("chats");
  const [convs, setConvs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [picker, setPicker] = useState(false);
  const [params] = useSearchParams();

  const loadConvs = useCallback(() => api.get("/messages/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([])), []);
  useEffect(() => {
    loadConvs();
    const iv = setInterval(loadConvs, 10000);
    return () => clearInterval(iv);
  }, [loadConvs]);
  useEffect(() => { const to = params.get("to"); if (to) setSelected(to); }, [params]);

  return (
    <div className="space-y-4">
      {picker && <ContactPicker onClose={() => setPicker(false)} onPick={(c) => { setSelected(c.id); setPicker(false); setTab("chats"); }} />}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Messages</h1>
          <p className="text-sm text-slate-500">Chat, announcements and video calls — all inside FSCD, no email needed.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="messages-tab-chats" onClick={() => setTab("chats")}
            className={`h-10 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${tab === "chats" ? "bg-teal-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            <MessageSquare size={14} /> Chats
          </button>
          <button data-testid="messages-tab-groups" onClick={() => setTab("groups")}
            className={`h-10 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${tab === "groups" ? "bg-teal-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            <Users size={14} /> Class Chats
          </button>
          <button data-testid="messages-tab-announcements" onClick={() => setTab("announcements")}
            className={`h-10 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${tab === "announcements" ? "bg-teal-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            <Megaphone size={14} /> Announcements
          </button>
        </div>
      </div>
      {tab === "announcements" ? <Announcements /> : tab === "groups" ? <GroupChats /> : (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden grid lg:grid-cols-[320px,1fr]" style={{ minHeight: "560px", maxHeight: "72vh" }}>
          <div className={`border-r border-slate-100 flex-col ${selected ? "hidden lg:flex" : "flex"}`}>
            <div className="p-3 border-b border-slate-100">
              <button data-testid="new-message-btn" onClick={() => setPicker(true)}
                className="w-full h-10 rounded-xl bg-teal-800 hover:bg-teal-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors">
                <Plus size={15} /> New message
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {!convs ? <p className="text-sm text-slate-400 p-4">Loading…</p> : convs.length === 0 ? (
                <p className="text-sm text-slate-400 p-5 text-center">No conversations yet. Start one with “New message”.</p>
              ) : convs.map((c) => (
                <button key={c.partner_id} data-testid={`conversation-${c.partner_id}`} onClick={() => setSelected(c.partner_id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 hover:bg-teal-50/40 transition-colors ${selected === c.partner_id ? "bg-teal-50/70" : ""}`}>
                  <Avatar name={c.partner_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800 truncate">{c.partner_name}</p>
                      <span className="text-[10px] text-slate-400 shrink-0">{new Date(c.last_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                    </div>
                    <p className="text-xs text-slate-400 truncate">{c.last_type === "call" ? "📹 Video call" : c.last_message}</p>
                  </div>
                  {c.unread > 0 && <span data-testid={`unread-badge-${c.partner_id}`} className="w-5 h-5 rounded-full bg-teal-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{c.unread}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className={`${selected ? "flex" : "hidden lg:flex"} flex-col`}>
            {selected ? <Thread partnerId={selected} onBack={() => { setSelected(null); loadConvs(); }} /> : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-300 gap-3 p-8">
                <MessageSquare size={44} strokeWidth={1.2} />
                <p className="text-sm text-slate-400">Select a conversation or start a new one</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
